import { useState, useEffect } from "react";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { KeyRound, ShieldAlert } from "lucide-react";

/**
 * Blocks the app until the user changes their password when the backend has
 * flagged their account with `password_change_required` (e.g. seeded admin,
 * legacy admin123 rotation).
 */
export default function ForcePasswordChangeModal() {
  const { user, changePassword, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const open = !!user?.password_change_required;

  useEffect(() => {
    if (!open) setForm({ current_password: "", new_password: "", confirm: "" });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) return toast.error("Yeni şifre en az 8 karakter olmalı");
    if (form.new_password !== form.confirm) return toast.error("Şifreler eşleşmiyor");
    setBusy(true);
    try {
      await changePassword(form.current_password, form.new_password);
      toast.success("Şifre değiştirildi. Bir sonraki girişte yeni şifrenizi kullanın.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Şifre değiştirilemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent className="max-w-md" data-testid="force-password-change-modal" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert size={18} className="text-[#D97706]" />
            Şifrenizi Güncelleyin
          </DialogTitle>
          <DialogDescription>
            Güvenlik nedeniyle hesabınız için yeni bir şifre belirlemeniz zorunludur. Devam etmek için lütfen yeni şifrenizi girin.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Mevcut Şifre</Label>
            <Input
              type="password"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              data-testid="current-password"
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <Label>Yeni Şifre (min. 8 karakter)</Label>
            <Input
              type="password"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              data-testid="new-password"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
          <div>
            <Label>Yeni Şifre (Tekrar)</Label>
            <Input
              type="password"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              data-testid="confirm-password"
              autoComplete="new-password"
              required
            />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={logout} data-testid="logout-from-pw-change">
              Çıkış Yap
            </Button>
            <Button type="submit" className="bg-[#1B4D3E] hover:bg-[#163e32] gap-2" disabled={busy} data-testid="submit-password-change">
              <KeyRound size={14} /> {busy ? "Güncelleniyor…" : "Şifreyi Değiştir"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
